import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Calendar, Users, ChevronDown, ChevronUp, Plane } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getSession } from '../../lib/supabase';
import { formatRelativeTime } from './ActivityLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

/**
 * Full-page component displayed at /trip/invite/:token.
 * Shows a trip preview and "Join This Trip" CTA.
 * Works without authentication.
 */
export default function InviteLanding({ token, onLoginRequest }) {
  const { user } = useAuth();
  const [tripData, setTripData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [expandedDays, setExpandedDays] = useState(new Set([0]));

  // Fetch trip preview (no auth needed)
  useEffect(() => {
    if (!token) return;

    fetch(`${BACKEND_URL}/api/trips/invite/${token}`)
      .then(res => {
        if (!res.ok) {
          if (res.status === 404) throw new Error('This invite link has expired or is invalid.');
          if (res.status === 410) throw new Error('This invite link has expired.');
          throw new Error('Failed to load trip preview.');
        }
        return res.json();
      })
      .then(data => setTripData(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Accept invite
  const handleAccept = useCallback(async () => {
    if (!user) {
      // Trigger login modal, then re-attempt after auth
      onLoginRequest?.(() => {
        // This callback is called after successful login
        handleAccept();
      });
      return;
    }

    setAccepting(true);
    try {
      // Get auth headers from localStorage (fast) or getSession() (fallback)
      const authHeaders = { 'Content-Type': 'application/json' };
      try {
        const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
        if (storageKey) {
          const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
          if (stored?.access_token) {
            authHeaders['Authorization'] = `Bearer ${stored.access_token}`;
          }
        }
        if (!authHeaders['Authorization']) {
          const sess = await getSession();
          if (sess?.access_token) {
            authHeaders['Authorization'] = `Bearer ${sess.access_token}`;
          }
        }
      } catch { /* proceed without auth */ }

      const res = await fetch(`${BACKEND_URL}/api/trips/invite/${token}/accept`, {
        method: 'POST',
        headers: authHeaders,
      });

      if (res.ok) {
        const data = await res.json();
        setAccepted(true);
        // Redirect to the trip after a brief moment
        setTimeout(() => {
          const tripId = data.tripId || data.trip_id || tripData?.trip_id;
          if (tripId) {
            window.location.href = `/?trip=${tripId}`;
          } else {
            window.location.href = '/';
          }
        }, 1500);
      } else {
        const errData = await res.json().catch(() => ({}));
        setError(errData.error || 'Failed to join trip. Please try again.');
      }
    } catch (err) {
      setError('Failed to join trip. Please try again.');
    } finally {
      setAccepting(false);
    }
  }, [user, token, tripData, onLoginRequest]);

  const toggleDay = useCallback((dayIndex) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayIndex)) next.delete(dayIndex);
      else next.add(dayIndex);
      return next;
    });
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="w-12 h-12 rounded-full bg-teal-200 mx-auto mb-4" />
          <div className="h-4 w-48 bg-gray-200 rounded mx-auto" />
        </div>
      </div>
    );
  }

  // Error state
  if (error && !tripData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <Plane className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to load trip</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <a
            href="/"
            className="inline-block px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors"
          >
            Plan your own trip
          </a>
        </div>
      </div>
    );
  }

  const {
    trip_name, trip_title, destination, destinations,
    dates, start_date, end_date,
    created_by, creator_name,
    collaborator_count,
    itinerary_data, itinerary_preview,
    role,
  } = tripData || {};

  const title = trip_name || trip_title || destination || 'Untitled Trip';
  const dest = destination || (destinations && destinations.join(', ')) || '';
  const days = itinerary_preview || itinerary_data?.days || itinerary_data || [];
  const dateDisplay = dates || (start_date && end_date ? `${start_date} - ${end_date}` : '');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero header */}
      <div className="relative bg-gradient-to-br from-teal-600 to-teal-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 40%)',
          }} />
        </div>

        <div className="relative max-w-2xl mx-auto px-6 py-16 text-center">
          <p className="text-teal-200 text-sm font-medium mb-2 uppercase tracking-wide">
            You're invited to join
          </p>
          <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {title}
          </h1>
          {dest && (
            <div className="flex items-center justify-center gap-2 text-teal-100 mb-4">
              <MapPin className="w-4 h-4" />
              <span className="text-lg">{dest}</span>
            </div>
          )}
          <div className="flex items-center justify-center gap-4 text-teal-200 text-sm">
            {dateDisplay && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {dateDisplay}
              </span>
            )}
            {(creator_name || created_by) && (
              <span>Created by {creator_name || created_by}</span>
            )}
          </div>
          {collaborator_count > 0 && (
            <div className="flex items-center justify-center gap-1 text-teal-200 text-sm mt-2">
              <Users className="w-4 h-4" />
              <span>{collaborator_count} collaborator{collaborator_count !== 1 ? 's' : ''}</span>
            </div>
          )}
          {role && (
            <p className="mt-3 text-xs text-teal-300">
              You'll join as: <span className="font-semibold capitalize">{role}</span>
            </p>
          )}
        </div>
      </div>

      {/* Day-by-day preview */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {Array.isArray(days) && days.length > 0 && (
          <div className="space-y-3 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Trip Itinerary</h2>
            {days.map((day, idx) => {
              const dayTitle = day.title || day.day_title || `Day ${idx + 1}`;
              const activities = day.activities || day.schedule || day.items || [];
              const isExpanded = expandedDays.has(idx);

              return (
                <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => toggleDay(idx)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{dayTitle}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <span className="text-xs">{activities.length} activities</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>

                  {isExpanded && activities.length > 0 && (
                    <div className="px-4 pb-3 border-t border-gray-100">
                      <div className="space-y-1.5 pt-2">
                        {activities.map((act, aIdx) => {
                          const name = act.name || act.activity || act.title || 'Activity';
                          const time = act.time || act.startTime || act.start_time || '';
                          return (
                            <div key={aIdx} className="flex items-center gap-2 text-sm">
                              {time && (
                                <span className="text-xs text-gray-400 w-14 shrink-0 text-right">
                                  {time}
                                </span>
                              )}
                              <span className="text-gray-700">{name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mb-8">
          {error && (
            <p className="text-sm text-red-500 mb-3">{error}</p>
          )}

          {accepted ? (
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-50 text-green-700 font-medium rounded-xl">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Joined! Redirecting to your trip...
            </div>
          ) : (
            <>
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="px-8 py-3.5 text-lg font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-lg shadow-teal-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {accepting ? 'Joining...' : 'Join This Trip'}
              </button>
              {!user && (
                <p className="text-sm text-gray-500 mt-3">
                  Sign in to collaborate on this trip
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bottom banner */}
      <div className="border-t border-gray-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-6 text-center">
          <p className="text-sm text-gray-500">
            This trip was planned by{' '}
            <span className="font-semibold text-gray-700">Arrivo AI</span>{' '}
            in 30 seconds
          </p>
          <a
            href="/"
            className="inline-block mt-2 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
          >
            Plan your own trip free &rarr;
          </a>
        </div>
      </div>
    </div>
  );
}
